import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import TrainingService, { TrainingPeriod } from '../../api/services/training.service';
import ToneChip from '../ui/ToneChip';

// One person's training, where it can be acted on: who trains them, from
// when, how it is going, and the button that ends it. The same card on the
// person's page. The days themselves run as the "Training" workflow in
// Operations: morning (with the trainer?), end of day (does it go on?),
// and, when it is finished, their work days and capacity.
//
// "Finished" is the moment payroll starts paying them normally: every day
// up to and including the last training day is a training day, whatever
// the statement says. Each day the trainer is also asked through a
// "Training check-in" workflow; an answer here does the same.

const today = () => new Date().toLocaleDateString('en-CA');
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—');
const describeError = (err: any, fallback: string) => err?.response?.data?.message || fallback;

export interface Person { accountId: string; name: string }

const TrainingCard: React.FC<{
  /** The person's newest period, or null when they have never been in training. */
  period: TrainingPeriod | null;
  accountId: string;
  name: string;
  people: Person[];
  editable: boolean;
  onChanged: (p: TrainingPeriod) => void;
  /** Hide the person's name (on their own page). */
  bare?: boolean;
}> = ({
  period, accountId, name, people, editable, onChanged, bare,
}) => {
  const [note, setNote] = useState('');
  const [startOn, setStartOn] = useState(today());
  const [endOn, setEndOn] = useState(today());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setNote(''); setError(null); }, [period?.id]);

  const run = async (fn: () => Promise<TrainingPeriod>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await fn());
      setNote('');
    } catch (err) {
      setError(describeError(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const active = period?.status === 'active';
  const trainers = people.filter((p) => p.accountId !== accountId);

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        {!bare && <Typography variant="subtitle2" sx={{ mr: 0.5 }}>{name}</Typography>}
        {active && <ToneChip tone="info" label={`In training · day ${period!.days}`} />}
        {period?.status === 'finished' && <ToneChip tone="ok" label={`Finished ${fmt(period.endedOn)}`} />}
        {period?.status === 'cancelled' && <ToneChip tone="neutral" label="Training cancelled" />}
        {!period && <Typography variant="body2" color="text.secondary">Not in training.</Typography>}
        {period && (
          <Typography variant="caption" color="text.secondary">
            Since {fmt(period.startedOn)}{period.source === 'hire' ? ', from hiring' : ''}
            {period.endedByName ? ` · ended by ${period.endedByName}` : ''}
          </Typography>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {active && editable && (
        <Stack gap={1.25}>
          <Stack direction="row" gap={1.25} flexWrap="wrap">
            <TextField
              select size="small" label="Trainer" value={period!.trainerId || ''} sx={{ minWidth: 200 }} disabled={busy}
              helperText="Asked each day how it is going"
              onChange={(e) => run(() => TrainingService.update(period!.id, { trainerId: e.target.value || null }), 'The trainer was not changed.')}
            >
              <MenuItem value=""><em>Any BC</em></MenuItem>
              {trainers.map((p) => <MenuItem key={p.accountId} value={p.accountId}>{p.name}</MenuItem>)}
            </TextField>
            <TextField
              size="small" type="date" label="First day" value={period!.startedOn.slice(0, 10)} disabled={busy} InputLabelProps={{ shrink: true }}
              onChange={(e) => e.target.value && run(() => TrainingService.update(period!.id, { startedOn: e.target.value }), 'The date was not changed.')}
            />
          </Stack>
          <TextField
            size="small" multiline minRows={2} label="How is it going?" placeholder="What they did today, what they still need" value={note}
            onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 1000 }} disabled={busy}
          />
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Button size="small" variant="outlined" disabled={busy} onClick={() => run(() => TrainingService.report(period!.id, { continuing: true, note }), 'The note was not saved.')}>
              Still in training
            </Button>
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small" type="date" label="Last training day" value={endOn} onChange={(e) => setEndOn(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 170 }} disabled={busy}
            />
            <Button
              size="small" variant="contained" disableElevation disabled={busy || !endOn}
              onClick={() => run(async () => {
                if (note.trim()) await TrainingService.report(period!.id, { continuing: true, note });
                return TrainingService.update(period!.id, { endedOn: endOn });
              }, 'Training was not finished.')}
            >
              Finish training
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Payroll pays training days up to the last training day, and normally from the day after.
          </Typography>
        </Stack>
      )}

      {!active && editable && (
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: period ? 1 : 0 }}>
          {period?.status === 'finished' && (
            <Button size="small" disabled={busy} onClick={() => run(() => TrainingService.update(period.id, { endedOn: null }), 'Training was not reopened.')}>
              Reopen
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <TextField size="small" type="date" label="First training day" value={startOn} onChange={(e) => setStartOn(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 170 }} disabled={busy} />
          <Button size="small" variant="outlined" disabled={busy || !startOn} onClick={() => run(() => TrainingService.start({ accountId, startedOn: startOn }), 'Training was not started.')}>
            {period ? 'Start training again' : 'Start training'}
          </Button>
        </Stack>
      )}

      {period && period.reports.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {period.reports.slice(0, 6).map((r) => (
            <Stack key={`${r.date}${r.note}${r.byAccountId}`} direction="row" gap={1} sx={{ py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ width: 56, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.date)}</Typography>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                {r.kind === 'morning' && <Box component="span" sx={{ fontWeight: 600 }}>{r.withTrainer ? 'With their trainer. ' : 'Not out. '}</Box>}
                {r.kind === 'schedule' && <Box component="span" sx={{ fontWeight: 600 }}>Schedule set. </Box>}
                {(r.kind || 'evening') === 'evening' && r.continuing === false && <Box component="span" sx={{ fontWeight: 700 }}>Finished. </Box>}
                {r.note || <Box component="span" sx={{ color: 'text.secondary' }}>{(r.kind || 'evening') === 'evening' && r.continuing ? 'Still in training.' : ''}</Box>}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{r.byName || ''}</Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TrainingCard;
